<script setup>
import AppBadge from '@/components/atoms/AppBadge'
import HelpHubLinkCard from '../HelpHubLinkCard.vue'
import { conversationSubject, statusLabel, statusVariant, supportTimeLabel, ticketTimeLabel, ticketTitle } from '@/helpers/support'
import { HELP_HUB_CHAT_PAGE } from '@/constants'

/**
 * The rows of one Chat list — past conversations, tickets or popular questions — on the
 * landing and on the list's own page alike. Figma draws all three with its one "Cards"
 * component, the Help tab's link card; a button is not a list item, so each row has its own `li`.
 */
defineProps({
  // Which list these are, named by its page.
  list: { type: String, required: true, validator: (list) => Object.values(HELP_HUB_CHAT_PAGE).includes(list) },
  // Conversations, or the questions' bare strings.
  items: { type: Array, required: true },
  // The landing packs its rows 8px apart so its three sections scroll less; a list's own page keeps 16.
  compact: { type: Boolean, default: false },
})

defineEmits(['select'])

// AppCard rings focus only for an inner link, so a card used as a button brings its own width, alignment and ring.
const rowClass = 'w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300'
</script>

<template>
  <ul class="flex flex-col" :class="compact ? 'gap-y-2' : 'gap-y-4'">
    <li v-for="item in items" :key="list === HELP_HUB_CHAT_PAGE.QUESTIONS ? item : item.id">
      <HelpHubLinkCard
        v-if="list === HELP_HUB_CHAT_PAGE.QUESTIONS"
        as="button"
        type="button"
        :class="rowClass"
        :title="item"
        :title-lines="2"
        icon="heroicons-outline:question-mark-circle"
        @click="$emit('select', item)"
      />

      <HelpHubLinkCard
        v-else-if="list === HELP_HUB_CHAT_PAGE.TICKETS"
        as="button"
        type="button"
        :class="rowClass"
        :icon="null"
        :title="ticketTitle(item)"
        :description="ticketTimeLabel(item)"
        @click="$emit('select', item)"
      >
        <template #trailing>
          <span class="flex items-center gap-x-2">
            <!-- Replies the dealer hasn't read: the count sits on the row, where the unanswered ticket is. -->
            <AppBadge v-if="Number(item.unreadCount) > 0" variant="tint-light" size="xs" pill>
              {{ item.unreadCount }}
            </AppBadge>

            <AppBadge :variant="statusVariant(item.status)" size="xs">
              {{ statusLabel(item.status) }}
            </AppBadge>
          </span>
        </template>
      </HelpHubLinkCard>

      <HelpHubLinkCard
        v-else
        as="button"
        type="button"
        :class="rowClass"
        :icon="null"
        :title="conversationSubject(item)"
        :description="supportTimeLabel(item.lastMessageAt ?? item.updatedAt ?? item.createdAt)"
        @click="$emit('select', item)"
      />
    </li>
  </ul>
</template>
